/**
 * Copyright (c) VVDN Technologies Pvt Ltd. All rights reserved.
 * Proprietary and confidential — custom algorithms.
 */

import type { FloorClientDot, FloorObstacle, FloorRoom, FloorWall, PlacedAp } from './types'
import { resolveObstacleFootprint } from './obstacles'
import type { FloorViewport } from './floorViewport'
import { DEFAULT_FLOOR_VIEWPORT } from './floorViewport'

export type FloorCanvasLayout = {
  left: number
  top: number
  /** Floor draw origin X within canvas element (px). */
  ox: number
  /** Floor draw origin Y within canvas element (px). */
  oy: number
  fw: number
  fh: number
  viewport?: FloorViewport
}

/** @deprecated use ox — kept for callers migrating gradually */
export type FloorCanvasLayoutLegacy = FloorCanvasLayout & { pad?: number }

function apMarkerCenter(ap: Pick<PlacedAp, 'x' | 'y'>, layout: FloorCanvasLayout) {
  const v = layout.viewport ?? DEFAULT_FLOOR_VIEWPORT
  const cx = layout.ox + (ap.x / 100) * layout.fw
  const cy = layout.oy + (ap.y / 100) * layout.fh
  return {
    ax: layout.left + v.panX + v.scale * cx,
    ay: layout.top + v.panY + v.scale * cy,
    scale: v.scale,
  }
}

/** Offset from AP center — matches drawAp() delete badge. */
export const AP_DELETE_BADGE = { dx: -16, dy: -14, r: 9, hitR: 10 }

export function hitApDeleteButton(
  clientX: number,
  clientY: number,
  aps: PlacedAp[],
  layout: FloorCanvasLayout,
): PlacedAp | null {
  for (let i = aps.length - 1; i >= 0; i--) {
    const ap = aps[i]
    const { ax, ay, scale } = apMarkerCenter(ap, layout)
    const bx = ax + AP_DELETE_BADGE.dx * scale
    const by = ay + AP_DELETE_BADGE.dy * scale
    if (Math.hypot(clientX - bx, clientY - by) <= AP_DELETE_BADGE.hitR * scale) return ap
  }
  return null
}

/** Match drawAp() bounds so AP wins over overlapping walls/obstacles/rooms. */
export function hitApMarker(
  clientX: number,
  clientY: number,
  aps: PlacedAp[],
  layout: FloorCanvasLayout,
): PlacedAp | null {
  for (let i = aps.length - 1; i >= 0; i--) {
    const ap = aps[i]
    const { ax, ay, scale } = apMarkerCenter(ap, layout)
    if (Math.hypot(clientX - ax, clientY - ay) <= 28 * scale) return ap
    if (
      clientX >= ax - 56 * scale &&
      clientX <= ax + 56 * scale &&
      clientY >= ay - 24 * scale &&
      clientY <= ay + 48 * scale
    ) {
      return ap
    }
    if ((ap.clients ?? 0) > 0 && Math.hypot(clientX - (ax + 16 * scale), clientY - (ay - 14 * scale)) <= 12 * scale) {
      return ap
    }
  }
  return null
}

export function distanceToWallSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x2 - x1
  const dy = y2 - y1
  const lenSq = dx * dx + dy * dy
  if (lenSq < 1e-6) return Math.hypot(px - x1, py - y1)
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  const nx = x1 + t * dx
  const ny = y1 + t * dy
  return Math.hypot(px - nx, py - ny)
}

export function distanceToNearestWall(px: number, py: number, walls: FloorWall[]): number {
  if (walls.length === 0) return Infinity
  let best = Infinity
  for (const w of walls) {
    const d = distanceToWallSegment(px, py, w.x1, w.y1, w.x2, w.y2)
    if (d < best) best = d
  }
  return best
}

export function hitWall(px: number, py: number, walls: FloorWall[], thresholdPct = 2.5): FloorWall | null {
  let best: { wall: FloorWall; d: number } | null = null
  for (const w of walls) {
    const d = distanceToWallSegment(px, py, w.x1, w.y1, w.x2, w.y2)
    if (d <= thresholdPct && (!best || d < best.d)) best = { wall: w, d }
  }
  return best?.wall ?? null
}

export function hitRoom(px: number, py: number, rooms: FloorRoom[]): FloorRoom | null {
  for (let i = rooms.length - 1; i >= 0; i--) {
    const r = rooms[i]
    if (px >= r.x && px <= r.x + r.w && py >= r.y && py <= r.y + r.h) return r
  }
  return null
}

export function hitObstacle(
  px: number,
  py: number,
  obstacles: FloorObstacle[],
  floorWidthM = 100,
  floorHeightM = 80,
): FloorObstacle | null {
  for (let i = obstacles.length - 1; i >= 0; i--) {
    const o = obstacles[i]
    const { w, h } = resolveObstacleFootprint(o, floorWidthM, floorHeightM)
    const rx = o.x - w / 2
    const ry = o.y - h / 2
    if (px >= rx && px <= rx + w && py >= ry && py <= ry + h) return o
  }
  return null
}

function clientMarkerCenter(client: Pick<FloorClientDot, 'x' | 'y'>, layout: FloorCanvasLayout) {
  const v = layout.viewport ?? DEFAULT_FLOOR_VIEWPORT
  const cx = layout.ox + (client.x / 100) * layout.fw
  const cy = layout.oy + (client.y / 100) * layout.fh
  return {
    x: layout.left + v.panX + v.scale * cx,
    y: layout.top + v.panY + v.scale * cy,
    scale: v.scale,
  }
}

export function hitClientMarker(
  clientX: number,
  clientY: number,
  clients: FloorClientDot[],
  layout: FloorCanvasLayout,
): FloorClientDot | null {
  for (let i = clients.length - 1; i >= 0; i--) {
    const c = clients[i]
    const { x, y, scale } = clientMarkerCenter(c, layout)
    if (Math.hypot(clientX - x, clientY - y) <= 14 * scale) return c
  }
  return null
}
